import { GetServerSidePropsContext } from "next";

import NewselaGlobalLayout from "@/base";
import RootProviders from "@/base/RootProviders";
import Veil from "@/components/Veil";
import ClassroomMainPage from "@/reports/teacher/classroom-data/ClassroomMainPage";
import InsightsLayout from "@/reports/teacher/common/InsightsLayout";
import SidebarNavigationList from "@/reports/teacher/common/SidebarNavigationList";
import useDisplaySideBar from "@/reports/teacher/utils/useDisplaySideBar";
import { InsightsSharedContextProvider } from "@/reports/teacher/utils/useInsightsSharedContext/useInsightsSharedContext";
import withInsightsPrivileges from "@/reports/teacher/utils/withInsightsPrivileges";
import { Classroom } from "@/types/models/Classroom.interface";
import { ListData } from "@/reports/components/ReadingSkillsDataList";
import { AdminReportingFiltersContextProvider } from "@/reports/admin/useAdminReportingFiltersContext/useAdminReportingFiltersContext";
import { getReadingSkillsProps } from "../student-data";

interface ClassroomInsightsPageProps {
  classrooms: Classroom[];
  loadingClasses: boolean;
  isVeiled: boolean;
  readingSkillsInitialProps: ListData;
}

const ClassroomInsightsPage = (props: ClassroomInsightsPageProps) => {
  const { classrooms, loadingClasses, isVeiled, readingSkillsInitialProps } =
    props;
  const displaySidebar = useDisplaySideBar();

  return (
    <NewselaGlobalLayout
      mainContents={
        <AdminReportingFiltersContextProvider>
          <InsightsSharedContextProvider
            classrooms={classrooms}
            loadingClasses={loadingClasses}
            isVeiled={isVeiled}
            readingSkillsInitialProps={readingSkillsInitialProps}
          >
            <Veil
              isVeiled={isVeiled}
              title="Unlock student performance"
              bodyText="Get actionable data on your student's reading activity and performance when you gain premium access to Newsela. Data shown below is an example using mock data."
            >
              <InsightsLayout displaySidebar={displaySidebar}>
                <InsightsLayout.InsightsDataLayout
                  displaySidebar={displaySidebar}
                >
                  <ClassroomMainPage isVeiled={isVeiled} />
                </InsightsLayout.InsightsDataLayout>
                {displaySidebar && (
                  <InsightsLayout.InsightsSideBarContainer>
                    <SidebarNavigationList />
                  </InsightsLayout.InsightsSideBarContainer>
                )}
              </InsightsLayout>
            </Veil>
          </InsightsSharedContextProvider>
        </AdminReportingFiltersContextProvider>
      }
    />
  );
};

export default withInsightsPrivileges({
  component: ClassroomInsightsPage,
});

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const RootProvidersInitialProps = await RootProviders.__getInitialProps({
    ctx,
  });
  const readingSkillsInitialProps = await getReadingSkillsProps();

  return {
    props: {
      [RootProviders.InitialPropsName]: RootProvidersInitialProps,
      readingSkillsInitialProps,
    },
  };
}
